import * as React from 'react'
import isEqual from 'lodash/isEqual'
import concat from 'lodash/concat'
import {
  type RetryAfterT,
  type OperationLocationT,
  fetchWithRetryAfter
} from '@frontrow/console/src/entities/helpers/operation'
import {exhaustive} from '@freckle/exhaustive'
import {maybe, mthen} from '@freckle/maybe'
import {type ParserT, record, nullable, array, oneOf, date} from '@freckle/parser'
import {useSafeCallback, useSafeEffectExtraDeps} from '@freckle/react-hooks'
import BugsnagHelper from '@frontrow/console/src/entities/helpers/exception-handlers/bugsnag-helper'
import {
  type Artifact,
  type GroupedArtifactData,
  artifactParser,
  groupArtifactData,
  fetchArtifactData
} from '@frontrow/console/src/entities/models/artifact'

type Props = {
  operationLocation: OperationLocationT
}

type Operation = {
  updatedAt: Date | undefined | null
  status: OperationStatus
  artifacts: Array<Artifact>
}

export type ArtifactDataState =
  | {
      tag: 'not_started'
    }
  | {
      tag: 'fetching'
    }
  | {
      tag: 'success'
      artifactData: GroupedArtifactData
    }
  | {
      tag: 'failed'
    }

type OperationStatus = 'not_started' | 'running' | 'succeeded' | 'failed'
const allOperationStatuses = [
  'not_started' as const,
  'running' as const,
  'succeeded' as const,
  'failed' as const
]

export const useOperation = ({
  operationLocation
}: Props): {
  operation: Operation | undefined | null
  artifactData: ArtifactDataState
} => {
  const [operation, setOperation] = React.useState<Operation | undefined | null>(null)
  const [retryAfterObj, setRetryAfterObj] = React.useState<
    | {
        retryAfter: RetryAfterT
        lastUpdated: Date | undefined | null
      }
    | undefined
    | null
  >(null)
  const [artifactData, setArtifactData] = React.useState<ArtifactDataState>({
    tag: 'not_started'
  })

  const getOperation = useSafeCallback(
    () => async () => {
      const {response, retryAfter: mRetryAfter} = await fetchWithRetryAfter<Operation>(
        operationLocation,
        operationParser
      )
      setOperation(response)

      const updateRetryAfter = () =>
        setRetryAfterObj(
          mthen(mRetryAfter, retryAfter => ({
            retryAfter,
            lastUpdated: response.updatedAt
          }))
        )

      switch (response.status) {
        case 'not_started':
          updateRetryAfter()
          break
        case 'running':
          updateRetryAfter()
          break
        case 'succeeded':
          setRetryAfterObj(null)
          setArtifactData({tag: 'fetching'})
          try {
            const data = await Promise.all(response.artifacts.map(fetchArtifactData))
            setArtifactData({
              tag: 'success',
              artifactData: groupArtifactData(concat(data))
            })
          } catch (e) {
            BugsnagHelper.logError(e as Error)
            setArtifactData({tag: 'failed'})
          }
          break
        case 'failed':
          setRetryAfterObj(null)
          break
        default:
          exhaustive(response.status)
      }
    },
    [operationLocation]
  )

  useSafeEffectExtraDeps(
    ({getOperation}: {getOperation: () => unknown}) => {
      getOperation() // initial get
    },
    [],
    {getOperation}
  )

  useSafeEffectExtraDeps(
    ({
      getOperation,
      retryAfterObj
    }: {
      getOperation: () => unknown
      retryAfterObj:
        | {
            retryAfter: RetryAfterT
            lastUpdated: Date | undefined | null
          }
        | null
        | undefined
    }) => {
      const setupRetry = (retryAfter: RetryAfterT): number => {
        switch (retryAfter.tag) {
          case 'delay-seconds':
            const timeoutMs = retryAfter.contents * 1000
            return window.setTimeout(getOperation, timeoutMs)
          case 'http-date':
            throw new Error('HTTP date header unsupported in Operations API')
          default:
            return exhaustive(retryAfter as never)
        }
      }

      return maybe(
        () => undefined, // No cleanup function need if polling is not setup
        ({retryAfter}: {retryAfter: RetryAfterT}) => {
          const timeoutId = setupRetry(retryAfter)
          return () => window.clearTimeout(timeoutId)
        },
        retryAfterObj
      )
    },
    [],
    {
      getOperation,
      retryAfterObj: {value: retryAfterObj, comparator: isEqual}
    }
  )

  return {operation, artifactData}
}

const operationParser: ParserT<Operation> = record({
  updatedAt: nullable(date()),
  status: oneOf('OperationStatus', allOperationStatuses),
  artifacts: array(artifactParser)
})
